import axios from 'axios';
import { UnifiedDeviceListResponse } from '../types/device';
import { HotelModeListConfigRequest, HotelModeConfigListResponse, HotelModeConfigInfoResponse, HotelModeAddConfigRequest, HotelModeConfigAddResponse, HotelModeExecutionHistoryRequest, HotelModeExecutionHistoryResponse, HotelModeExecutionInfoResponse } from '../types/hotelMode';

// NOTE: Keep TGMaxx related helpers in this module (no self-imports)

// Interface matching the Java DeviceInfoRequest model
interface DeviceInfoRequest {
  sn: string;
  source: string;
  includeLockStatus?: boolean;
  includeTimestamps?: boolean;
}

// export const API_BASE_URL = 'http://localhost:8080';

export const API_BASE_URL = 'https://spring-backend-134362959982.us-central1.run.app';

console.log('API_BASE_URL:', API_BASE_URL);

// Simple in-memory auth token and helpers. We set axios default header when token is present.
export function setAuthToken(token: string | null) {
    try {
        if (token) {
            localStorage.setItem('jwt', token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            localStorage.removeItem('jwt');
            delete axios.defaults.headers.common['Authorization'];
        }
    } catch (e) {
        // ignore storage errors
    }
}

export function loadStoredToken() {
    try {
        const t = localStorage.getItem('jwt');
        if (t) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
        }
    } catch (e) {
        // ignore
    }
}

// Attempt to load token at module init
loadStoredToken();

// Global axios response interceptor: if any request returns 403, treat as logged out.
axios.interceptors.response.use(
    (res) => res,
    (err) => {
        try {
            const status = err?.response?.status;
            if (status === 403) {
                // clear stored token and notify app that user must re-login
                try { setAuthToken(null); } catch (e) { /* ignore */ }
                try { window.dispatchEvent(new CustomEvent('auth:logout', { detail: { status: 403 } })); } catch (e) { /* ignore */ }
            }
        } catch (e) {
            // ignore
        }
        return Promise.reject(err);
    }
);

/**
 * Call backend auth login endpoint. Returns the full axios response so caller can check status.
 */
export async function login(username: string, password: string) {
    const url = `${API_BASE_URL}/auth/login`;
    try {
        // Use a fresh axios instance so any global/default Authorization header is not sent for the login request.
        // Note: axios.create() copies axios.defaults, so explicitly remove Authorization on the instance.
        console.log("Sending login request for user:", username);

        const plainAxios = axios.create();
        // remove any Authorization header copied from global defaults
        try {
            delete plainAxios.defaults.headers.common['Authorization'];
        } catch (e) {
            // ignore if header path doesn't exist
        }

        // Debug: show that the instance no longer has Authorization header (dev only)
        console.log("Plain axios defaults (post-clean):", plainAxios.defaults);

        const res = await plainAxios.post(url, { username, password }, { validateStatus: () => true });

        return res; // caller checks res.status and res.data
    } catch (err: any) {
        // propagate error in a simplified form
        throw new Error(err?.message || 'Login request failed');
    }
}

export const fetchData = async (endpoint: string) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/${endpoint}`);
        return response.data;
    } catch (error: any) {
        throw new Error(`Error fetching data: ${error?.message || error}`);
    }
};

export const postData = async (endpoint: string, data: any) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/${endpoint}`, data);
        return response.data;
    } catch (error: any) {
        throw new Error(`Error posting data: ${error?.message || error}`);
    }
};

// Fetch unified devices list from backend based on tenant
export async function fetchDevices(params: {
    page?: number;
    limit?: number;
    offset?: number;
    search?: string;
    mac?: string;
    sn?: string;
    tenant?: string;
}): Promise<UnifiedDeviceListResponse> {
    const { tenant = 'TGStream', page = 1, limit = 20, search, mac, sn } = params;
    
    const qp = new URLSearchParams();
    
    // Convert page-based pagination to offset-based
    const offset = (page - 1) * limit;
    qp.set('offset', String(offset));
    qp.set('limit', String(limit));
    
    // Handle search parameters
    if (search) {
        // If search contains MAC pattern (xx:xx:xx:xx:xx:xx), treat as MAC search
        if (/^[0-9a-fA-F]{2}([:-]?[0-9a-fA-F]{2}){5}$/.test(search)) {
            qp.set('mac', search);
        } else {
            qp.set('sn', search);
        }
    }
    
    if (mac) qp.set('mac', mac);
    if (sn) qp.set('sn', sn);

    // Determine API endpoint based on tenant
    let endpoint: string;
    switch (tenant.toLowerCase()) {
        case 'tgmaxx':
            endpoint = '/api/tgmaxx/devices';
            break;
        case 'tgstreamtest':
            endpoint = '/api/tgstream/devices';
            break;
        case 'tgstreamtl4kbr':
            endpoint = '/api/tgstream/devices';
            break;
        case 'tgstream':
        default:
            endpoint = '/api/tgstream/devices';
            break;
    }

    const url = `${API_BASE_URL}${endpoint}?${qp.toString()}`;
    const res = await axios.get(url);
    return res.data as UnifiedDeviceListResponse;
}

// Add more API functions as needed

export interface UnifiedDeviceControlRequest {
    source?: string;
    deviceCommandEnum?: string;
    deviceId?: string;
    params?: Record<string, any>;
}

export interface SingleDeviceLogcatRequest {
    sn: string;
    logLevel?: string;
    stopUploadTime?: number;
    packageNames?: string;
    catchLogType?: number;
    uploadFileSize?: number | string;
    uploadPeriod?: number;
    captureRange?: number;
}

export interface DeviceLogListEntity {
    sn: string;
}

export interface DeviceLogListRequest {
    entity: DeviceLogListEntity;
    pageNum: number;
    pageSize: number;
    orderType?: string;
    sortColumns?: string;
}

export interface DeviceLogListItem {
    id?: string;
    sn?: string;
    downloadUrl?: string;
    uploadStatus?: number;
    createTime?: number;
    fileId?: string;
}

export interface DeviceLogListResponse {
    code?: number;
    businessCode?: string;
    message?: string;
    time?: number;
    data?: {
        total?: number;
        list?: DeviceLogListItem[];
        pageNum?: number;
        pageSize?: number;
    };
}

export interface TgmaxxGroupListRequest {
    offset?: number;
    limit?: number;
}

export interface TgmaxxGroupListInfo {
    id?: number;
    groupType?: string;
    groupName?: string;
    description?: string;
    manufacturer?: string;
    modelId?: number;
    model?: string;
    createUser?: string;
    createTime?: string;
    updateTime?: string;
    cfg?: string;
}

export interface TgmaxxGroupListResponse {
    code?: number;
    message?: string;
    data?: TgmaxxGroupListInfo[];
}

// Types for TGMaxx GroupAction list
export interface GroupActionListRequest {
    token?: string | null; // server will set token when calling backend if needed
    modelName?: string;
    type?: number | null;
    status?: number | null;
    offset?: number;
    limit?: number;
}

export interface GroupActionListItem {
    id?: number;
    name?: string;
    modelName?: string;
    type?: number;
    status?: number;
    startTime?: number;
    endTime?: number;
    createTime?: number;
    updateTime?: number;
    groupId?: number;
}

export interface GroupActionListResponse {
    code?: number;
    message?: string;
    data?: {
        total?: number;
        rows?: GroupActionListItem[];
    } | GroupActionListItem[] | any;
}

/**
 * Fetch TGMaxx group list from backend.
 * Endpoint: POST /api/tgmaxx/group/queryGroupList
 */
export async function fetchTgmaxxGroupList(params: TgmaxxGroupListRequest): Promise<TgmaxxGroupListResponse> {
    const url = `${API_BASE_URL}/api/tgmaxx/group/queryGroupList`;
    const res = await axios.post(url, params);
    return res.data as TgmaxxGroupListResponse;
}

/**
 * Fetch TGMaxx group action list from backend.
 * Endpoint: POST /api/tgmaxx/groupAction/list
 */
export async function postTgmaxxGroupActionList(req: GroupActionListRequest): Promise<GroupActionListResponse> {
    const url = `${API_BASE_URL}/api/tgmaxx/groupAction/list`;
    const res = await axios.post(url, req);
    return res.data as GroupActionListResponse;
}

/**
 * Push a TGMaxx group action to backend.
 * Endpoint: POST /api/tgmaxx/groupAction/push
 * Payload: { token?: string|null, mac?: string, groupName?: string, taskName?: string }
 */
export interface GroupActionPushRequest {
    token?: string | null;
    mac?: string;
    groupName?: string;
    taskName?: string;
}

export interface GroupActionPushResponse {
    code?: number;
    message?: string;
    data?: any;
}

export async function postTgmaxxGroupActionPush(req: GroupActionPushRequest): Promise<GroupActionPushResponse> {
    const url = `${API_BASE_URL}/api/tgmaxx/groupAction/push`;
    const res = await axios.post(url, req);
    return res.data as GroupActionPushResponse;
}

/**
 * Post a control request to the backend /api/device/control
 * Expects a UnifiedDeviceControlRequest-like payload
 */
export async function postDeviceControl(req: UnifiedDeviceControlRequest) {
    const url = `${API_BASE_URL}/api/device/control`;
    // Make sure params is always an object (not null) as backend expects a map
    const payload = { ...req, params: req.params ?? {} };
    const res = await axios.post(url, payload);
    return res.data;
}

/**
 * Send logcat request to backend: POST /api/device/logcat
 */
export async function postDeviceLogcat(req: SingleDeviceLogcatRequest) {
    const url = `${API_BASE_URL}/api/device/logcat`;
    const res = await axios.post(url, req);
    return res.data;
}

/**
 * Fetch device log list: POST /api/device/logs/list
 */
export async function postDeviceLogList(req: DeviceLogListRequest) {
    const url = `${API_BASE_URL}/api/device/logs/list`;
    const res = await axios.post(url, req);
    return res.data as DeviceLogListResponse;
}


/**
 * Enable remote screen for a device by calling the remote service through the gateway at port 3000.
 * The backend returns a token payload (accessToken / refreshToken) inside data.
 */
export async function enableRemoteScreen(sn: string) {
    // Ensure caller provided a non-empty SN string. The remote endpoint expects
    // a JSON body like { "sn": "<SN>" }.
    if (!sn || typeof sn !== 'string' || sn.trim() === '') {
        throw new Error('enableRemoteScreen requires a valid device SN');
    }
    const url = `${API_BASE_URL}/api/device/remote`;
    const payload = { sn: sn };
    const res = await axios.post(url, payload);
    return res.data;
}

/**
 * Fetch extended device info from backend.
 * The backend expects a POST to /api/device/info with a DeviceInfoRequest object.
 */
export async function fetchDeviceInfo(device: any) {
    const url = `${API_BASE_URL}/api/device/info`;

    // Deduplicate concurrent requests for the same device so we don't hit the backend
    // multiple times (React StrictMode/dev mounts can trigger duplicate effects).
    // Use a simple in-memory map keyed by device SN or id.
    const sn = device?.sn ?? device?.id ?? '';
    const key = `${sn}`;

    // Map stored at module-level for in-flight promises
    if (!(fetchDeviceInfo as any)._inflight) {
        (fetchDeviceInfo as any)._inflight = new Map<string, Promise<any>>();
    }
    const inflight: Map<string, Promise<any>> = (fetchDeviceInfo as any)._inflight;
    if (inflight.has(key)) {
        return inflight.get(key) as Promise<any>;
    }

    const promise = (async () => {
        try {
            // Create DeviceInfoRequest object with required fields
            const deviceInfoRequest: DeviceInfoRequest = {
                sn: sn,
                source: device?.source || 'TGStream', // Default to TGStream if not specified
                includeLockStatus: true,
                includeTimestamps: true
            };
            
            const res = await axios.post(url, deviceInfoRequest);
            return res.data;
        } finally {
            // clear entry regardless of success/failure so subsequent calls can retry
            inflight.delete(key);
        }
    })();

    inflight.set(key, promise);
    return promise;
}

/**
 * Call traceroute endpoint on backend: POST /api/device/traceroute
 * Payload should be a TraceRouteTerminalRequest-like object (sn, ip, domain, timeout)
 */
export async function postTraceRoute(payload: { sn?: string; ip?: string; domain?: string; timeout?: string | number }) {
    const url = `${API_BASE_URL}/api/device/traceroute`;
    const res = await axios.post(url, payload);
    return res.data;
}

export interface PushConfigRequest {
    mac?: string;
    groupName?: string;
    taskName?: string;
}

export async function postLauncherConfigPush(payload: PushConfigRequest) {
    const url = `${API_BASE_URL}/api/tgmaxx/launcherConfig/push`;
    const res = await axios.post(url, payload);
    return res.data;
}

export async function postCleanAppDataConfigPush(payload: PushConfigRequest) {
    const url = `${API_BASE_URL}/api/tgmaxx/cleanAppData/push`;
    const res = await axios.post(url, payload);
    return res.data;
}

export async function postPushMessageConfigPush(payload: PushConfigRequest) {
    const url = `${API_BASE_URL}/api/tgmaxx/message/push`;
    const res = await axios.post(url, payload);
    return res.data;
}

export interface WirelessMacBatchRequest {
    sns: string[];
}

export interface WirelessMacBatchResponse {
    code?: number;
    message?: string;
    data?: Record<string, string>;
}

export async function postWirelessMacBatch(payload: WirelessMacBatchRequest): Promise<WirelessMacBatchResponse> {
    const url = `${API_BASE_URL}/api/tgmaxx/wirelessMac/batch`;
    const res = await axios.post(url, payload);
    return res.data as WirelessMacBatchResponse;
}

export interface TgmaxxGetGroupRequest {
    mac?: string;
    sn?: string;
}

export interface TgmaxxGetGroupResponse {
    code?: number;
    message?: string;
    data?: { groupName?: string; groupId?: number } | null;
}

export async function postTgmaxxGetGroup(payload: TgmaxxGetGroupRequest): Promise<TgmaxxGetGroupResponse> {
    const url = `${API_BASE_URL}/api/tgmaxx/getGroup`;
    const res = await axios.post(url, payload);
    return res.data as TgmaxxGetGroupResponse;
}

export interface DeviceCityRequest {
    sn: string;
}

export interface DeviceCityResponse {
    sn?: string;
    city?: string;
}

export async function postDeviceCity(source: string | undefined, payload: DeviceCityRequest): Promise<DeviceCityResponse> {
    const isTgMaxx = (source ?? '').toLowerCase() === 'tgmaxx';
    const url = `${API_BASE_URL}/api/${isTgMaxx ? 'tgmaxx' : 'tgstream'}/deviceCity`;
    const res = await axios.post(url, payload);
    return res.data as DeviceCityResponse;
}

/**
 * Call ping endpoint on backend: POST /api/device/ping
 * Payload: { sn, ip, domain, repeatTimes, timeout }
 */
export async function postPing(payload: { sn?: string; ip?: string; domain?: string; repeatTimes?: string | number; timeout?: string | number }) {
    const url = `${API_BASE_URL}/api/device/ping`;
    const res = await axios.post(url, payload);
    return res.data;
}

/**
 * Call speed test endpoint on backend: POST /api/device/speedtest
 * Payload: { sn }
 */
export async function postSpeedTest(payload: { mac?: string; downloadUrl?: string }) {
    const url = `${API_BASE_URL}/api/device/speedTest`;
    const res = await axios.post(url, payload);
    return res.data;
}

export interface RemoteScreenWebrtcAddressRequest {
    sn: string;
    token?: string;
}

export interface RemoteScreenWebrtcAddressResponse {
    code?: number;
    message?: string;
    data?: string;
}

export interface StopWebrtcByMacRequest {
    mac: string;
}

export enum RemoteControlCommand {
    HOME = 3,
    RETURN = 4,
    UP = 19,
    DOWN = 20,
    LEFT = 21,
    RIGHT = 22,
    OK = 23,
    VOLUME_UP = 24,
    VOLUME_DOWN = 25,
    MUTE = 164,
    POWER = 177,
    APPS = 284,
}

export interface RemoteControlBySnRequest {
    sn: string;
    command: RemoteControlCommand;
}

export interface RemoteControlBySnResponse {
    code?: number;
    message?: string;
    data?: any;
}

/**
 * Fetch remote screen WebRTC address for TGMaxx.
 * Endpoint: POST /api/tgmaxx/remoteScreen/webrtcAddress
 */
export async function postRemoteScreenWebrtcAddress(payload: RemoteScreenWebrtcAddressRequest): Promise<RemoteScreenWebrtcAddressResponse> {
    const url = `${API_BASE_URL}/api/tgmaxx/remoteScreen/webrtcAddress`;
    const res = await axios.post(url, payload);
    return res.data as RemoteScreenWebrtcAddressResponse;
}

/**
 * Stop TGMaxx WebRTC stream by SN.
 * Endpoint: POST /api/tgmaxx/remoteScreen/stopWebrtcByMac
 */
export async function postStopWebrtcByMac(payload: StopWebrtcByMacRequest) {
    const url = `${API_BASE_URL}/api/tgmaxx/remoteScreen/stopWebrtcByMac`;
    const res = await axios.post(url, payload);
    return res.data;
}

/**
 * Send remote control command for TGMaxx device by SN.
 * Endpoint: POST /api/tgmaxx/remoteControlBySn
 */
export async function postRemoteControlBySn(payload: RemoteControlBySnRequest): Promise<RemoteControlBySnResponse> {
    const url = `${API_BASE_URL}/api/tgmaxx/remoteControlBySn`;
    const res = await axios.post(url, payload);
    return res.data as RemoteControlBySnResponse;
}

/**
 * Update launcher custom text for TGMaxx devices.
 * Endpoint: POST /api/tgmaxx/updateLauncherCustomText
 * Payload: { mac, customText }
 */
export async function postLauncherCustomText(payload: { mac: string; customText: string }) {
    const url = `${API_BASE_URL}/api/tgmaxx/updateLauncherCustomText`;
    const res = await axios.post(url, payload);
    return res.data;
}

/**
 * Fetch currently authenticated user's details from backend.
 * Endpoint: GET /api/user/details
 */
export async function fetchUserDetails() {
    const url = `${API_BASE_URL}/api/user/details`;
    const res = await axios.get(url);
    return res.data;
}

/**
 * Fetch App Data Configuration list from backend.
 * Endpoint: POST /api/hotelmode/config/list
 */
export async function fetchHotelModeConfigList(params: {
    page?: number;
    pageSize?: number;
    configName?: string;
}): Promise<HotelModeConfigListResponse> {
    const { page = 1, pageSize = 20, configName } = params;
    
    const url = `${API_BASE_URL}/api/hotelmode/config/list`;
    
    const request: HotelModeListConfigRequest = {
        entity: configName ? { configName } : undefined,
        pageNum: page,
        pageSize: pageSize,
        orderType: 'asc',
        sortColumns: 'configName'
    };
    
    const res = await axios.post(url, request);
    return res.data as HotelModeConfigListResponse;
}

/**
 * Fetch Hotel Mode Configuration details by ID from backend.
 * Endpoint: POST /api/hotelmode/config/info
 */
export async function fetchHotelModeConfigInfo(configId: string): Promise<HotelModeConfigInfoResponse> {
    const url = `${API_BASE_URL}/api/hotelmode/config/info`;
    const res = await axios.post(url, configId, {
        headers: {
            'Content-Type': 'text/plain'
        }
    });
    return res.data as HotelModeConfigInfoResponse;
}

/**
 * Add a new Hotel Mode Configuration.
 * Endpoint: POST /api/hotelmode/config/add
 */
export async function addHotelModeConfig(request: HotelModeAddConfigRequest): Promise<HotelModeConfigAddResponse> {
    const url = `${API_BASE_URL}/api/hotelmode/config/add`;
    const res = await axios.post(url, request);
    return res.data as HotelModeConfigAddResponse;
}

/**
 * Fetch hotel mode execution history list with pagination
 * Endpoint: POST /api/hotelmode/execution/history
 */
export async function fetchHotelModeExecutionHistory(request: HotelModeExecutionHistoryRequest): Promise<HotelModeExecutionHistoryResponse> {
    const url = `${API_BASE_URL}/api/hotelmode/execution/history`;
    const res = await axios.post(url, request);
    return res.data as HotelModeExecutionHistoryResponse;
}

/**
 * Fetch hotel mode execution info details by execution ID
 * Endpoint: POST /api/hotelmode/execution/info
 */
export async function fetchHotelModeExecutionInfo(executionId: string): Promise<HotelModeExecutionInfoResponse> {
    const url = `${API_BASE_URL}/api/hotelmode/execution/info`;
    const res = await axios.post(url, executionId, {
        headers: {
            'Content-Type': 'text/plain'
        }
    });
    return res.data as HotelModeExecutionInfoResponse;
}

/**
 * Delete a Hotel Mode Configuration.
 * Endpoint: POST /api/hotelmode/config/delete
 */
export async function deleteHotelModeConfig(configId: string): Promise<any> {
    const url = `${API_BASE_URL}/api/hotelmode/config/delete`;
    const res = await axios.post(url, configId, {
        headers: {
            'Content-Type': 'text/plain'
        }
    });
    return res.data;
}

/**
 * Execute a Hotel Mode Configuration.
 * Endpoint: GET /api/hotelmode/execute
 */
export async function executeHotelModeConfig(configId: string): Promise<any> {
    const url = `${API_BASE_URL}/api/hotelmode/execute`;
    const res = await axios.get(url, {
        params: {
            configId: configId
        }
    });
    return res.data;
}
